import * as R from 'remeda'

/**
 * 配列内のオブジェクトを指定されたキーの値をキーとするオブジェクトに変換します。
 *
 * @template T - オブジェクトの型。
 * @template U - 変換後のオブジェクトの型。
 * @template K - キーの型。
 * @param items - オブジェクトの配列。
 * @param fn - 変換関数。
 * @param key - キー。
 * @returns 変換後のオブジェクト。
 */
export function mapItemsToObj<
  T extends { [k in K]: number | string },
  U,
  K extends keyof T,
>(items: T[], fn: (item: T) => U, key: K): Record<T[K], U>

/**
 * 配列内のオブジェクトを指定されたキーの値をキーとするオブジェクトに変換します。
 *
 * @template T - オブジェクトの型。
 * @template U - 変換後のオブジェクトの型。
 * @param items - "id" をキーに持つオブジェクトの配列
 * @param fn - 変換関数。
 * @returns 変換後のオブジェクト。
 */
export function mapItemsToObj<T extends { id: number | string }, U>(
  items: T[],
  fn: (item: T) => U,
): Record<T['id'], U>

/**
 * 配列内のオブジェクトを指定されたキーの値をキーとするオブジェクトに変換します。
 *
 * @template T - オブジェクトの型。
 * @template U - 変換後のオブジェクトの型。
 * @template K - キーの型。
 * @param fn - 変換関数。
 * @param key - キー。
 * @returns オブジェクトの配列を引数に取り、変換後のオブジェクトを返す関数。
 */
export function mapItemsToObj<
  T extends { [k in K]: number | string },
  U,
  K extends keyof T,
>(fn: (item: T) => U, key: K): (items: T[]) => Record<T[K], U>

/**
 * 配列内のオブジェクトを指定されたキーの値をキーとするオブジェクトに変換します。
 *
 * @template T - オブジェクトの型。
 * @template U - 変換後のオブジェクトの型。
 * @param fn - 変換関数。
 * @returns "id" をキーに持つオブジェクトの配列を引数に取り、変換後のオブジェクトを返す関数。
 */
export function mapItemsToObj<T extends { id: number | string }, U>(
  fn: (item: T) => U,
): (items: T[]) => Record<T['id'], U>

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function mapItemsToObj<
  T extends { [k in K]: number | string },
  U,
  K extends keyof T,
>(
  ...args:
    | [(item: T) => U, K]
    | [(item: T) => U]
    | [T[], (item: T) => U, K]
    | [T[], (item: T) => U]
): Record<T[K], U> | ((items: T[]) => Record<T[K], U>) {
  const mapItemsToObjInner = (items: T[], fn: (item: T) => U, key: K) =>
    R.mapToObj(items, (item) => [item[key], fn(item)])

  if (args.length === 1) {
    // args: [fn: (item: T) => U]
    return (items: T[]) => mapItemsToObjInner(items, ...args, 'id' as K)
  }

  if (args.length === 2) {
    if (typeof args[0] === 'function') {
      // args: [fn: (item: T) => U, key: K]
      return (items: T[]) =>
        mapItemsToObjInner(items, ...(args as [(item: T) => U, K]))
    }

    // args: [items: T[], fn: (item: T) => U]
    return mapItemsToObjInner(...(args as [T[], (item: T) => U]), 'id' as K)
  }

  // args: [items: T[], fn: (item: T) => U, key: K]
  return mapItemsToObjInner(...args)
}
